import React, { useState } from 'react';
import UsersTable from './UsersTable';
import InviteUserForm from './InviteUserForm';
import OrganizationsPanel from './OrganizationsPanel';
import AuditPanel from './AuditPanel';
import InventorySignersPanel from './InventorySignersPanel';

interface AdminPageProps {
    authToken: string;
    currentUserUid?: string;
}

// BAR-135: extends the BAR-126 panel with Organizations (per-client entitlement
// toggles) and Audit (reviewable admin actions incl. impersonation_* and
// update_org_modules).
type Tab = 'users' | 'invite' | 'organizations' | 'signers' | 'audit';

const TAB_LABELS: Record<Tab, string> = {
    users: 'Users',
    invite: 'Invite',
    organizations: 'Organizations',
    signers: 'Inventory signers',
    audit: 'Audit',
};

const TABS: Tab[] = ['users', 'invite', 'organizations', 'signers', 'audit'];

function AdminPage({ authToken, currentUserUid }: AdminPageProps) {
    const [tab, setTab] = useState<Tab>('users');
    // When an invite succeeds we trigger a refresh on the users table so
    // the newly-invited user appears immediately.
    const [usersRefreshKey, setUsersRefreshKey] = useState(0);

    return (
        <div className="flex flex-col h-full bg-bg p-6 overflow-y-auto">
            <header className="mb-6">
                <h1 className="text-2xl font-semibold text-fg">Admin</h1>
                <p className="text-sm text-fg-secondary mt-1">
                    Manage users, invitations, roles, and organization entitlements.
                </p>
            </header>

            <nav className="flex gap-2 border-b border-border-default mb-6" role="tablist">
                {TABS.map((t) => (
                    <button
                        key={t}
                        role="tab"
                        aria-selected={tab === t}
                        onClick={() => setTab(t)}
                        className={
                            'px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ' +
                            (tab === t
                                ? 'border-accent text-accent'
                                : 'border-transparent text-fg-secondary hover:text-fg')
                        }
                    >
                        {TAB_LABELS[t]}
                    </button>
                ))}
            </nav>

            <div className="flex-1">
                {tab === 'users' && (
                    <UsersTable
                        authToken={authToken}
                        currentUserUid={currentUserUid}
                        refreshKey={usersRefreshKey}
                    />
                )}
                {tab === 'invite' && (
                    <InviteUserForm
                        authToken={authToken}
                        onInvited={() => {
                            setUsersRefreshKey((k) => k + 1);
                        }}
                    />
                )}
                {tab === 'organizations' && (
                    <OrganizationsPanel authToken={authToken} />
                )}
                {tab === 'signers' && (
                    <InventorySignersPanel />
                )}
                {tab === 'audit' && (
                    <AuditPanel authToken={authToken} />
                )}
            </div>
        </div>
    );
}

export default AdminPage;
